import { Company } from '../../company/company.model';
import { TenantAccessSummary } from '../../company/tenant-access.model';

export interface SisterCompanyScope {
    companyGroupId: number;
    companyGroupName: string;
    sisterCompanies: Company[];
}

export function isSisterCompanyResource(
    companyId: number | null | undefined,
    scope: SisterCompanyScope | null,
    selectedCompanyId: number | null = null
): boolean {
    return companyId != null
        && (selectedCompanyId == null || companyId === selectedCompanyId)
        && !!scope?.sisterCompanies.some(company => company.id === companyId);
}

export function sisterCompanyFleetAccessMessage(resource: 'driver' | 'vehicle' | 'both'): string {
    if (resource === 'driver') {
        return 'You need Fleet Drivers View in the company group or sister company to load sister-company drivers.';
    }
    if (resource === 'vehicle') {
        return 'You need Fleet Vehicles View in the company group or sister company to load sister-company vehicles.';
    }
    return 'You need Fleet Drivers View and Fleet Vehicles View in the company group or sister company to load sister-company resources.';
}

export function resolveSisterCompanyScope(
    summary: TenantAccessSummary | null | undefined,
    currentCompanyId: number | null | undefined,
    requiredPermissions: readonly string[] = []
): SisterCompanyScope | null {
    if (!summary || !currentCompanyId) {
        return null;
    }

    const groups = (summary.companyGroups ?? []).filter(candidate =>
        candidate.active !== false
        && (candidate.memberCompanies ?? []).some(company => company.id === currentCompanyId)
    );
    const hasRequiredPermissions = (roles: Array<{ permissionCodes: string[] }> | undefined): boolean =>
        requiredPermissions.every(permission => (roles ?? []).some(role =>
            (role.permissionCodes ?? []).includes(permission)));
    const directSisters = requiredPermissions.length
        ? (summary.companies ?? []).filter(access => access.active !== false
            && access.sourceCompanyGroupId != null
            && access.companyId !== currentCompanyId
            && hasRequiredPermissions(access.roles))
        : [];
    const group = groups.find(candidate => hasRequiredPermissions(candidate.roles))
        ?? groups.find(candidate => directSisters.some(access => access.sourceCompanyGroupId === candidate.companyGroupId));
    const companyGroupId = group?.companyGroupId ?? directSisters[0]?.sourceCompanyGroupId;
    if (companyGroupId == null) return null;

    const groupWideAccess = !!group && hasRequiredPermissions(group.roles);
    const sisterCompanies = groupWideAccess
        ? (group.memberCompanies ?? []).filter(company => company?.id && company.id !== currentCompanyId)
        : directSisters.filter(access => access.sourceCompanyGroupId === companyGroupId)
            .map(access => ({ id: access.companyId, name: access.companyName } as Company));
    if (!sisterCompanies.length) {
        return null;
    }

    return {
        companyGroupId,
        companyGroupName: group?.companyGroupName || 'Sister companies',
        sisterCompanies
    };
}
